import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    getDistrictList,
    getBlockList,
    getGpList,
    getIsgppSchemeWiseExpenditure,
} from "../../Service/TotalAvailableFund";

const SchemeWiseExpenditure = () => {
    const getCurrentDate = () => new Date().toISOString().split("T")[0];

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState(getCurrentDate());

    const [district, setDistrict] = useState("");
    const [block, setBlock] = useState("");
    const [gp, setGp] = useState("");
    const [reportFor, setReportFor] = useState("");

    const [districtList, setDistrictList] = useState([]);
    const [blockList, setBlockList] = useState([]);
    const [gpList, setGpList] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadDistricts = async () => {
            try {
                const response = await getDistrictList();
                setDistrictList(response?.data || []);
            } catch (error) {
                console.error("District list error:", error);
                toast.error("Failed to load district list");
            }
        };

        loadDistricts();
    }, []);

    const onDistrict = async (event) => {
        const selectedDistrict = event.target.value;

        setDistrict(selectedDistrict);
        setBlock("");
        setGp("");
        setBlockList([]);
        setGpList([]);
        setReportData([]);

        if (!selectedDistrict) return;

        try {
            const response = await getBlockList(selectedDistrict);
            setBlockList(response?.data || []);
        } catch (error) {
            console.error("Block list error:", error);
            toast.error("Failed to load block list");
        }
    };

    const onBlock = async (event) => {
        const selectedBlock = event.target.value;

        setBlock(selectedBlock);
        setGp("");
        setGpList([]);
        setReportData([]);

        if (!selectedBlock) return;

        try {
            const response = await getGpList(selectedBlock);
            setGpList(response?.data || []);
        } catch (error) {
            console.error("GP list error:", error);
            toast.error("Failed to load Gram Panchayat list");
        }
    };

    const onGp = (event) => {
        setGp(event.target.value);
        setReportData([]);
    };

    const onReportFor = (event) => {
        setReportFor(event.target.value);
        setReportData([]);
    };

    const rows = useMemo(() => {
        if (Array.isArray(reportData)) return reportData;
        if (Array.isArray(reportData?.dtls)) return reportData.dtls;
        if (Array.isArray(reportData?.data)) return reportData.data;
        if (Array.isArray(reportData?.result)) return reportData.result;
        return [];
    }, [reportData]);

    const fixedColumns = [
        "lgdCode",
        "priName",
        "total",
        "rowTotal",
        "grandTotal",
    ];

    const schemeColumns = useMemo(() => {
        return Array.from(
            new Set(
                rows.flatMap((row) =>
                    Object.keys(row || {}).filter(
                        (key) => !fixedColumns.includes(key)
                    )
                )
            )
        );
    }, [rows]);

    const getNumericValue = (value) => {
        if (value === undefined || value === null || value === "") return 0;

        const numericValue = Number(String(value).replace(/,/g, ""));
        return Number.isNaN(numericValue) ? 0 : numericValue;
    };

    const formatAmount = (value) =>
        getNumericValue(value).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const formatHeader = (header) =>
        String(header || "")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/_/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .replace(/\b\w/g, (character) => character.toUpperCase());

    const getColumnTotal = (columnName) =>
        rows.reduce(
            (sum, row) => sum + getNumericValue(row?.[columnName]),
            0
        );

    const getRowTotal = (row) =>
        schemeColumns.reduce(
            (sum, column) => sum + getNumericValue(row?.[column]),
            0
        );

    const getGrandTotal = () =>
        rows.reduce((sum, row) => sum + getRowTotal(row), 0);

    const getReportForName = () => {
        if (reportFor === "1") return "Zilla Parishad";
        if (reportFor === "2") return "Panchayat Samiti";
        if (reportFor === "3") return "Gram Panchayat";
        return "PRI";
    };

    const validateSearch = () => {
        if (!reportFor) {
            toast.error("Please select Report For option");
            return false;
        }

        if (!fromDate) {
            toast.error("Please select From Date");
            return false;
        }

        if (!toDate) {
            toast.error("Please select To Date");
            return false;
        }

        if (fromDate > toDate) {
            toast.error("From Date cannot be greater than To Date");
            return false;
        }

        return true;
    };

    const onSearch = async () => {
        if (!validateSearch()) return;

        setLoading(true);
        setReportData([]);

        try {
            const response = await getIsgppSchemeWiseExpenditure(
                district || 0,
                block || 0,
                gp || 0,
                reportFor,
                fromDate,
                toDate
            );

            if (response?.status !== 200) {
                toast.error("Failed to fetch data");
                return;
            }

            const responseData = response?.data || {};
            const responseRows = Array.isArray(responseData)
                ? responseData
                : responseData?.dtls ||
                responseData?.data ||
                responseData?.result ||
                [];

            setReportData(responseData);

            if (responseRows.length === 0) {
                toast.info("No data found");
            } else {
                toast.success("Data fetched successfully");
            }
        } catch (error) {
            console.error("Scheme-wise expenditure error:", error);
            toast.error(
                error?.response?.data?.statusMsg ||
                error?.response?.data?.message ||
                "Failed to fetch data"
            );
        } finally {
            setLoading(false);
        }
    };

    const splitPriName = (priName) => {
        const normalizedName = String(priName || "")
            .replace(/\u00A0/g, " ")
            .replace(/\s+/g, " ")
            .trim();

        if (!normalizedName.includes("::")) {
            return {
                pri: normalizedName,
                block: "",
                gp: "",
            };
        }

        const parts = normalizedName.split("::");

        return {
            pri: normalizedName,
            block: parts[0]?.trim() || "",
            gp: parts.slice(1).join("::").trim(),
        };
    };

    const exportToExcel = () => {
        if (!rows || rows.length === 0) {
            toast.error("No data available to export");
            return;
        }

        const isGpReport = reportFor === "3";

        const worksheetRows = rows.map((row, index) => {
            const priDetails = splitPriName(row?.priName);

            let excelRow;

            if (isGpReport) {
                /*
                 * GP report column order:
                 * SL No. → Block → Gram Panchayat → LGD Code
                 * → Scheme columns → Total
                 */
                excelRow = {
                    "SL No.": index + 1,
                    Block: priDetails?.block || "",
                    "Gram Panchayat": priDetails?.gp || "",
                    "LGD Code": row?.lgdCode ?? "",
                };
            } else {
                /*
                 * PS/District report column order:
                 * SL No. → PRI → LGD Code
                 * → Scheme columns → Total
                 */
                excelRow = {
                    "SL No.": index + 1,
                    PRI: priDetails?.pri || row?.priName || "",
                    "LGD Code": row?.lgdCode ?? "",
                };
            }

            // Scheme-wise expenditure columns
            schemeColumns.forEach((column) => {
                excelRow[formatHeader(column)] = getNumericValue(
                    row?.[column]
                );
            });

            excelRow.Total = getRowTotal(row);

            return excelRow;
        });

        let grandTotalRow;

        if (isGpReport) {
            grandTotalRow = {
                "SL No.": "",
                Block: "Grand Total",
                "Gram Panchayat": "",
                "LGD Code": "",
            };
        } else {
            grandTotalRow = {
                "SL No.": "",
                PRI: "Grand Total",
                "LGD Code": "",
            };
        }

        schemeColumns.forEach((column) => {
            grandTotalRow[formatHeader(column)] =
                getColumnTotal(column);
        });

        grandTotalRow.Total = getGrandTotal();

        const worksheet = XLSX.utils.json_to_sheet([
            ...worksheetRows,
            grandTotalRow,
        ]);

        // Set column widths in the same order as the exported columns
        worksheet["!cols"] = isGpReport
            ? [
                { wch: 8 },  // SL No.
                { wch: 28 }, // Block
                { wch: 32 }, // Gram Panchayat
                { wch: 15 }, // LGD Code

                ...schemeColumns.map(() => ({
                    wch: 22,
                })),

                { wch: 20 }, // Total
            ]
            : [
                { wch: 8 },  // SL No.
                { wch: 40 }, // PRI
                { wch: 15 }, // LGD Code

                ...schemeColumns.map(() => ({
                    wch: 22,
                })),

                { wch: 20 }, // Total
            ];

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Scheme Expenditure"
        );

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(
            blob,
            `Scheme_Wise_Expenditure_${fromDate}_to_${toDate}.xlsx`
        );
    };

    return (
        <div>
            <ToastContainer />

            <div
                className="bg-white rounded-lg p-2 flex flex-col flex-grow"
                style={{ marginTop: "-40px" }}
            >
                <legend className="text-lg font-semibold text-cyan-700">
                    Scheme Wise Expenditure
                </legend>

                <div className="flex flex-col space-y-2 py-1">
                    <div className="flex flex-col w-full mb-4 space-y-2">
                        <div className="flex items-center flex-wrap gap-y-2">
                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    District
                                </label>
                                <select
                                    autoComplete="off"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    value={district}
                                    onChange={onDistrict}
                                >
                                    <option value="">Select District</option>
                                    {districtList.map((item) => (
                                        <option
                                            key={item.DIST_LGD}
                                            value={item.DIST_LGD}
                                        >
                                            {item.DIST_NAME}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Block
                                </label>
                                <select
                                    autoComplete="off"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    value={block}
                                    onChange={onBlock}
                                    disabled={!district}
                                >
                                    <option value="">Select Block</option>
                                    {blockList.map((item) => (
                                        <option
                                            key={item.BLOCK_LGD}
                                            value={item.BLOCK_LGD}
                                        >
                                            {item.BLOCK_NAME}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Gram Panchayat
                                </label>
                                <select
                                    autoComplete="off"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    value={gp}
                                    onChange={onGp}
                                    disabled={!block}
                                >
                                    <option value="">
                                        Select Gram Panchayat
                                    </option>
                                    {gpList.map((item) => (
                                        <option
                                            key={item.GP_LGD}
                                            value={item.GP_LGD}
                                        >
                                            {item.GP_NAME}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Report For{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <select
                                    autoComplete="off"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    value={reportFor}
                                    onChange={onReportFor}
                                >
                                    <option value="">Select Report For</option>
                                    <option value="1">ZP</option>
                                    <option value="2">PS</option>
                                    <option value="3">GP</option>
                                </select>
                            </div>

                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    From Date{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    value={fromDate}
                                    onChange={(event) => {
                                        setFromDate(event.target.value);
                                        setReportData([]);
                                    }}
                                    max={toDate || getCurrentDate()}
                                />
                            </div>

                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    To Date{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    value={toDate}
                                    onChange={(event) => {
                                        setToDate(event.target.value);
                                        setReportData([]);
                                    }}
                                    min={fromDate || ""}
                                    max={getCurrentDate()}
                                />
                            </div>

                            <div className="w-1/6 px-2">
                                <button
                                    type="button"
                                    className="btn-submit h-9 px-4 mt-5 shadow-sm text-white hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60"
                                    onClick={onSearch}
                                    disabled={loading}
                                >
                                    {loading ? "Loading..." : "Search"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div
                            className="flex items-center justify-center bg-gray-200"
                            style={{ marginTop: "10px", minHeight: "80px" }}
                        >
                            <h1 className="text-xl font-semibold text-black-800">
                                Loading...
                            </h1>
                        </div>
                    ) : rows.length === 0 ? (
                        <div
                            className="flex items-center justify-center bg-gray-200"
                            style={{ marginTop: "10px", minHeight: "80px" }}
                        >
                            <h1 className="text-xl font-semibold text-black-800">
                                No Data Found
                            </h1>
                        </div>
                    ) : (
                        <div className="overflow-x-auto p-4">
                            <div className="flex justify-between items-center mb-2">
                                <button
                                    type="button"
                                    onClick={exportToExcel}
                                    className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 transition"
                                >
                                    Download Excel
                                </button>

                                <h2 className="text-lg font-semibold text-gray-800 text-center flex-1">
                                    {getReportForName()} Scheme Wise Expenditure
                                </h2>
                            </div>

                            <div className="border rounded overflow-hidden">
                                <div className="max-h-[500px] overflow-auto relative">
                                    <table className="min-w-full divide-y divide-gray-200 border text-sm">
                                        <thead className="bg-cyan-600 text-white sticky top-0 z-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left bg-cyan-600 whitespace-nowrap">
                                                    SL No.
                                                </th>
                                                <th className="px-4 py-2 text-left bg-cyan-600 whitespace-nowrap">
                                                    LGD Code
                                                </th>
                                                <th className="px-4 py-2 text-left bg-cyan-600 min-w-[280px]">
                                                    PRI
                                                </th>

                                                {schemeColumns.map(
                                                    (column) => (
                                                        <th
                                                            key={column}
                                                            className="px-4 py-2 text-right bg-cyan-600 min-w-[180px] whitespace-nowrap uppercase"
                                                        >
                                                            {formatHeader(column)}
                                                        </th>
                                                    )
                                                )}

                                                <th className="px-4 py-2 text-right bg-cyan-700 min-w-[180px] whitespace-nowrap">
                                                    Total
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {rows.map((row, index) => (
                                                <tr
                                                    key={`${row?.lgdCode || "row"}-${index}`}
                                                    className="bg-white hover:bg-gray-50"
                                                >
                                                    <td className="px-4 py-2 whitespace-nowrap">
                                                        {index + 1}
                                                    </td>
                                                    <td className="px-4 py-2 whitespace-nowrap">
                                                        {row?.lgdCode}
                                                    </td>
                                                    <td className="px-4 py-2 font-medium min-w-[280px]">
                                                        {row?.priName}
                                                    </td>

                                                    {schemeColumns.map(
                                                        (column) => (
                                                            <td
                                                                key={column}
                                                                className="px-4 py-2 text-right whitespace-nowrap"
                                                            >
                                                                {formatAmount(
                                                                    row?.[column]
                                                                )}
                                                            </td>
                                                        )
                                                    )}

                                                    <td className="px-4 py-2 text-right font-bold bg-gray-100 whitespace-nowrap">
                                                        {formatAmount(
                                                            getRowTotal(row)
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>

                                        <tfoot className="bg-gray-300 font-semibold text-black sticky bottom-0 z-40">
                                            <tr>
                                                <td className="px-4 py-2 bg-gray-300" />
                                                <td className="px-4 py-2 bg-gray-300" />
                                                <td className="px-4 py-2 text-left bg-gray-300">
                                                    Grand Total
                                                </td>

                                                {schemeColumns.map(
                                                    (column) => (
                                                        <td
                                                            key={column}
                                                            className="px-4 py-2 text-right bg-gray-300 whitespace-nowrap"
                                                        >
                                                            {formatAmount(
                                                                getColumnTotal(
                                                                    column
                                                                )
                                                            )}
                                                        </td>
                                                    )
                                                )}

                                                <td className="px-4 py-2 text-right font-bold bg-gray-400 whitespace-nowrap">
                                                    {formatAmount(
                                                        getGrandTotal()
                                                    )}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SchemeWiseExpenditure;